/**
 * measureRead — wraps one UI read so it becomes observable.
 *
 * THIS MEASURES. IT FIXES NOTHING. The slow cost queries are untouched.
 *
 * Generic on purpose: the contract is a shape key, a filter object, and
 * `() => Promise<T>`. That is satisfiable by any read — a table select, an RPC,
 * a REST fetch, or a composite of several — so a surface adopts this without
 * the wrapper knowing anything about cost, calls or money.
 *
 * Why it does not go through `logger`'s shipping path: `sendToServer` applies
 * `isRateLimited()`, which SILENTLY DROPS beyond 10 logs/second. A dropped
 * sample would make the counts wrong, and a count that is sampled cannot answer
 * "how often". Here every sample is either delivered or counted in
 * `dropped_before`, so the total is always exact. `logger` is still used for the
 * one thing it is right for: reporting at ERROR when the instrument itself
 * fails.
 */

import logger from '@/lib/logger';

import {
  AUTHENTICATED_STATEMENT_TIMEOUT_MS,
  buildQueryTelemetryRecord,
  type QueryTelemetryRecord,
} from './queryTelemetry';

/**
 * Buffer cap. Chosen from the delivery contract, not taste: the telemetry route
 * accepts at most 250 records per POST (see app/api/telemetry/query/route.ts),
 * so holding more than one postable batch cannot help — it can only grow
 * unboundedly in a tab left open, which is precisely the situation that
 * produced hours of post-call spend on this product.
 */
const TELEMETRY_BUFFER_MAX = 250;

interface BufferedRecord extends QueryTelemetryRecord {
  /** Samples this client discarded before this one. Exact, never estimated. */
  dropped_before: number;
}

let buffer: BufferedRecord[] = [];
let droppedSinceLastKept = 0;

/** Test seam only. */
export function __resetQueryTelemetryForTests(): void {
  buffer = [];
  droppedSinceLastKept = 0;
}

function currentSurface(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    return window.location.pathname || 'unknown';
  } catch {
    return 'unknown';
  }
}

export interface MeasureReadOptions<T> {
  /** Extracts a row count from the reader's result. Failure yields null, never 0. */
  rowCount?: (result: T) => number | null;
  /** The role ceiling this read is measured against. Defaults to authenticated. */
  budgetMs?: number;
  surface?: string;
}

function record<T>(
  shapeKey: string,
  filters: Record<string, unknown>,
  elapsedMs: number,
  error: unknown,
  result: T | undefined,
  opts: MeasureReadOptions<T>,
): void {
  let rowCount: number | null = null;
  if (error === null && opts.rowCount && result !== undefined) {
    try {
      const n = opts.rowCount(result);
      rowCount = typeof n === 'number' && Number.isFinite(n) ? n : null;
    } catch {
      // An extractor that throws must not cost us the sample, and must not
      // fabricate a count. Unknown stays null.
      rowCount = null;
    }
  }

  const built = buildQueryTelemetryRecord({
    shapeKey,
    surface: opts.surface ?? currentSurface(),
    elapsedMs: Math.round(elapsedMs),
    error,
    rowCount,
    filters,
    budgetMs: opts.budgetMs ?? AUTHENTICATED_STATEMENT_TIMEOUT_MS,
  });

  if (buffer.length >= TELEMETRY_BUFFER_MAX) {
    // Buffer full: this sample is lost, but it is COUNTED and the count rides
    // on the next sample we do keep, so emitted + dropped is always exact.
    droppedSinceLastKept += 1;
    return;
  }
  buffer.push({ ...built, dropped_before: droppedSinceLastKept });
  droppedSinceLastKept = 0;
  scheduleTelemetryDelivery();
}

/**
 * Record one UI-state sample (not a read) into the same buffer and delivery:
 * a page reaching its rendered state, a crash screen shown. `failed` maps to
 * outcome 'failed'; `flags` become filter_shape.active names (true = present).
 */
export function recordUiTelemetrySample(input: {
  shapeKey: string;
  elapsedMs: number;
  budgetMs: number;
  failed: boolean;
  flags?: Record<string, boolean>;
  surface?: string;
}): void {
  record(
    input.shapeKey,
    input.flags ?? {},
    input.elapsedMs,
    input.failed ? new Error('ui_state_failed') : null,
    undefined,
    { budgetMs: input.budgetMs, surface: input.surface },
  );
}

/**
 * Drain the buffer. Returns and clears, so a batch is never delivered twice.
 *
 * Any drop count still outstanding is folded onto the LAST record of the
 * drained batch. Without this the count strands: once the buffer is full no
 * further record is kept, so there is nothing left to carry the tally and the
 * drops would vanish — which is exactly the silent loss this field exists to
 * prevent. If the batch is empty the tally is held for the next one rather than
 * discarded.
 */
export function takeTelemetryBatch(): BufferedRecord[] {
  const out = buffer;
  buffer = [];
  if (droppedSinceLastKept > 0 && out.length > 0) {
    out[out.length - 1].dropped_before += droppedSinceLastKept;
    droppedSinceLastKept = 0;
  }
  return out;
}

/**
 * Time a read and record it — success or failure — without changing what the
 * read does. The reader's result is returned untouched and its error re-thrown
 * unwrapped, so callers keep their existing error handling.
 */
export async function measureRead<T>(
  shapeKey: string,
  filters: Record<string, unknown>,
  reader: () => Promise<T>,
  opts: MeasureReadOptions<T> = {},
): Promise<T> {
  const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const elapsed = () =>
    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started;
  try {
    const result = await reader();
    try {
      record(shapeKey, filters, elapsed(), null, result, opts);
    } catch (instrumentError) {
      // Every failsafe logs as an ERROR: a blind instrument is the defect.
      logger.error('measureRead', 'telemetry record failed on a successful read', {
        shapeKey,
        error: instrumentError instanceof Error ? instrumentError.message : String(instrumentError),
      });
    }
    return result;
  } catch (readError) {
    try {
      record(shapeKey, filters, elapsed(), readError ?? new Error('unknown read failure'), undefined, opts);
    } catch (instrumentError) {
      logger.error('measureRead', 'telemetry record failed on a failing read', {
        shapeKey,
        error: instrumentError instanceof Error ? instrumentError.message : String(instrumentError),
      });
    }
    throw readError;
  }
}

/**
 * The shape supabase-js resolves with: it does NOT throw on a query error.
 *
 * Note the reader is typed `PromiseLike<T>`, not `Promise<T>`: a
 * PostgrestFilterBuilder is a thenable and lacks `catch`/`finally`, so
 * demanding a full Promise would reject every real call site. And T is inferred
 * rather than constrained, so callers keep the precise response type they had
 * — constraining it to this interface erased `error.code` at the call sites.
 */
export interface SupabaseLikeResponse {
  data: unknown;
  error: unknown;
}

/**
 * Time a supabase-js read.
 *
 * Distinct from `measureRead` for one load-bearing reason: supabase-js
 * RESOLVES with `{ data, error }` instead of rejecting, so wrapping it with a
 * throw-based timer records every SQLSTATE 57014 as a successful read. That is
 * how a page can time out for weeks while its telemetry reports health — the
 * exact failure this instrument exists to prevent. Here the embedded error is
 * lifted out and classified, and the response object is returned untouched so
 * callers keep their existing `if (error) throw error` handling.
 */
export async function measureSupabaseRead<T>(
  shapeKey: string,
  filters: Record<string, unknown>,
  reader: () => PromiseLike<T>,
  opts: Omit<MeasureReadOptions<T>, 'rowCount'> = {},
): Promise<T> {
  const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const elapsed = () =>
    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started;
  let response: T;
  try {
    response = await reader();
  } catch (transportError) {
    // A rejection here is a transport failure (network, abort), not a query
    // error — still a sample, still with its elapsed time.
    try {
      record(shapeKey, filters, elapsed(), transportError ?? new Error('unknown transport failure'), undefined, opts);
    } catch (instrumentError) {
      logger.error('measureSupabaseRead', 'telemetry record failed on a rejected read', {
        shapeKey,
        error: instrumentError instanceof Error ? instrumentError.message : String(instrumentError),
      });
    }
    throw transportError;
  }

  try {
    const shape = (response ?? {}) as { data?: unknown; error?: unknown };
    const embedded = shape.error ?? null;
    const rows = Array.isArray(shape.data) ? shape.data.length : null;
    record(shapeKey, filters, elapsed(), embedded, response, {
      ...opts,
      rowCount: () => rows,
    } as MeasureReadOptions<T>);
  } catch (instrumentError) {
    logger.error('measureSupabaseRead', 'telemetry record failed', {
      shapeKey,
      error: instrumentError instanceof Error ? instrumentError.message : String(instrumentError),
    });
  }
  return response;
}

// ============================================================================
// DELIVERY
// The batch goes to /api/telemetry/query, which persists it. Delivery is
// fire-and-forget: it must never block or fail a read. It must, however, be
// LOGGED when it fails, because an instrument that has gone blind silently is
// the defect being fixed.
// ============================================================================

const FLUSH_INTERVAL_MS = 5000;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

export async function flushTelemetry(): Promise<void> {
  const batch = takeTelemetryBatch();
  if (batch.length === 0) return;
  try {
    const res = await fetch('/api/telemetry/query', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ records: batch }),
      keepalive: true,
    });
    if (!res.ok) {
      logger.error('queryTelemetry', 'telemetry batch rejected by the sink', {
        status: res.status,
        records: batch.length,
      });
    }
  } catch (err: unknown) {
    logger.error('queryTelemetry', 'telemetry batch could not be delivered', {
      records: batch.length,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

function scheduleFlush(): void {
  if (typeof window === 'undefined') return;
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushTelemetry();
  }, FLUSH_INTERVAL_MS);
}

let unloadRegistered = false;
function registerFlushOnUnload(): void {
  if (typeof window === 'undefined' || unloadRegistered) return;
  unloadRegistered = true;
  window.addEventListener('pagehide', () => { void flushTelemetry(); });
}

/** Call after recording so samples reach the sink without blocking the read. */
export function scheduleTelemetryDelivery(): void {
  registerFlushOnUnload();
  scheduleFlush();
}
